import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import BookingModal from '../components/BookingModal';
import './bookings.css';
import PageLayout from '../components/pagelayout';

// ── helpers ────────────────────────────────────────────────────────────────────

const TYPE_LABEL = { day: 'Regular', vip: 'VIP', student: 'Student', family: 'Family' };
const TYPE_COLOR = { day: 'blue', vip: 'gold', student: 'green', family: 'purple' };

function fmt(date) {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function StatCard({ icon, label, value, sub, subUp }) {
  return (
    <div className="bk-stat">
      <div className="bk-stat-icon">{icon}</div>
      <div className="bk-stat-body">
        <span className="bk-stat-label">{label}</span>
        <span className="bk-stat-value">{value}</span>
        {sub && (
          <span className={`bk-stat-sub ${subUp ? 'up' : 'down'}`}>
            {subUp ? '↑' : '↓'} {sub}
          </span>
        )}
      </div>
    </div>
  );
}

// ── main component ─────────────────────────────────────────────────────────────

const PAGE_SIZE = 8;

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Modal state
  const [modal, setModal] = useState(null); // null | 'create' | booking object (edit)

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);

  // View detail
  const [viewTarget, setViewTarget] = useState(null);

  // ── data ────────────────────────────────────────────────────────────────────

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('bookings')
      .select('*', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);

    if (search.trim()) {
      query = query.or(
        `full_name.ilike.%${search}%,email.ilike.%${search}%,id.eq.${isNaN(search) ? 0 : search}`
      );
    }
    if (filterType !== 'all') {
      query = query.eq('ticket_type', filterType);
    }

    const { data, count, error } = await query;
    if (!error) {
      setBookings(data || []);
      setTotal(count || 0);
    }
    setLoading(false);
  }, [page, search, filterType]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, filterType]);

  // Stats
  const [stats, setStats] = useState({ total: 0, revenue: 0 });
  useEffect(() => {
    supabase.from('bookings').select('quantity, ticket_type').then(({ data }) => {
      if (!data) return;
      const PRICE = { day: 150, vip: 350, student: 80, family: 250 };
      const rev = data.reduce((s, b) => s + (PRICE[b.ticket_type] || 0) * b.quantity, 0);
      setStats({ total: data.length, revenue: rev });
    });
  }, [bookings]);

  // ── CRUD handlers ────────────────────────────────────────────────────────────

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  const handleSave = async (form) => {
    if (modal?.id) {
      // UPDATE
      const { error } = await supabase.from('bookings').update(form).eq('id', modal.id);
      if (error) { showToast('Failed to update booking.', 'error'); return; }
      showToast('Booking updated successfully.');
    } else {
      // CREATE
      const { error } = await supabase.from('bookings').insert([form]);
      if (error) { showToast('Failed to create booking.', 'error'); return; }
      showToast('Booking created successfully.');
    }
    setModal(null);
    fetchBookings();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    const { error } = await supabase.from('bookings').delete().eq('id', deleteTarget.id);
    setDeleteLoading(false);
    if (error) { showToast('Failed to delete booking.', 'error'); return; }
    showToast('Booking deleted.');
    setDeleteTarget(null);
    fetchBookings();
  };

  // ── pagination ───────────────────────────────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const exportCSV = () => {
    const header = ['ID', 'Name', 'Email', 'Phone', 'Date', 'Ticket Type', 'Quantity', 'Payment'];
    const rows = bookings.map(b => [
      b.id, b.full_name, b.email, b.phone, b.date, b.ticket_type, b.quantity, b.payment_method,
    ]);
    const csv = [header, ...rows].map(r => r.join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = 'bookings.csv';
    a.click();
  };

  // ── render ───────────────────────────────────────────────────────────────────

  return (
    <PageLayout>
    <div className="bk-root">
      {/* Toast */}
      {toast && (
        <div className={`bk-toast bk-toast--${toast.type}`}>
          {toast.type === 'success' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
          )}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="bk-header">
        <div>
          <h1 className="bk-title">Bookings</h1>
          <p className="bk-subtitle">View and manage all exhibition bookings</p>
        </div>
        <div className="bk-header-actions">
          <button className="bk-btn-export" onClick={exportCSV}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
            Export
          </button>
          <button className="bk-btn-new" onClick={() => setModal('create')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
            New Booking
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="bk-stats">
        <StatCard
          icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>}
          label="Total Bookings"
          value={stats.total.toLocaleString()}
        />
        <StatCard
          icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>}
          label="This Page"
          value={bookings.length}
        />
        <StatCard
          icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>}
          label="Ticket Types"
          value="4 Types"
        />
        <StatCard
          icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>}
          label="Est. Revenue"
          value={`${(stats.revenue / 1000).toFixed(0)}K EGP`}
          sub="+18.7% from last month"
          subUp
        />
      </div>

      {/* Toolbar */}
      <div className="bk-toolbar">
        <div className="bk-search-wrap">
          <svg className="bk-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            className="bk-search"
            type="text"
            placeholder="Search by name, email or ID..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button className="bk-search-clear" onClick={() => setSearch('')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          )}
        </div>

        <div className="bk-filters">
          {['all', 'day', 'vip', 'student', 'family'].map(t => (
            <button
              key={t}
              className={`bk-filter-btn ${filterType === t ? 'active' : ''}`}
              onClick={() => setFilterType(t)}
            >
              {t === 'all' ? 'All' : TYPE_LABEL[t]}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bk-table-wrap">
        <table className="bk-table">
          <thead>
            <tr>
              <th>Booking ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Ticket Type</th>
              <th>Qty</th>
              <th>Date</th>
              <th>Payment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className="bk-skeleton-row">
                  {Array.from({ length: 8 }).map((__, j) => (
                    <td key={j}><span className="bk-skeleton" /></td>
                  ))}
                </tr>
              ))
            ) : bookings.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <div className="bk-empty">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                    <p>No bookings found</p>
                    <span>{search || filterType !== 'all' ? 'Try adjusting your filters' : 'Create your first booking to get started'}</span>
                  </div>
                </td>
              </tr>
            ) : (
              bookings.map(b => (
                <tr key={b.id} className="bk-row">
                  <td>
                    <span className="bk-id">#{b.id}</span>
                  </td>
                  <td>
                    <div className="bk-name-cell">
                      <div className="bk-avatar">
                        {(b.full_name || '?').charAt(0).toUpperCase()}
                      </div>
                      <span>{b.full_name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="bk-email">{b.email}</span>
                  </td>
                  <td>
                    <span className={`bk-badge bk-badge--${TYPE_COLOR[b.ticket_type] || 'blue'}`}>
                      {TYPE_LABEL[b.ticket_type] || b.ticket_type}
                    </span>
                  </td>
                  <td>
                    <span className="bk-qty-val">{b.quantity}</span>
                  </td>
                  <td>
                    <span className="bk-date">{fmt(b.date)}</span>
                  </td>
                  <td>
                    <span className={`bk-payment bk-payment--${b.payment_method}`}>
                      {b.payment_method === 'card' ? '💳' : b.payment_method === 'wallet' ? '👛' : '💵'}
                      {' '}{b.payment_method}
                    </span>
                  </td>
                  <td>
                    <div className="bk-actions">
                      <button
                        className="bk-action bk-action--view"
                        title="View details"
                        onClick={() => setViewTarget(b)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      </button>
                      <button
                        className="bk-action bk-action--edit"
                        title="Edit booking"
                        onClick={() => setModal(b)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button
                        className="bk-action bk-action--delete"
                        title="Delete booking"
                        onClick={() => setDeleteTarget(b)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="bk-pagination">
        <span className="bk-page-info">
          Showing {bookings.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} bookings
        </span>
        <div className="bk-page-btns">
          <button
            className="bk-page-btn"
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
            let p;
            if (totalPages <= 5) p = i + 1;
            else if (page <= 3) p = i + 1;
            else if (page >= totalPages - 2) p = totalPages - 4 + i;
            else p = page - 2 + i;
            return (
              <button
                key={p}
                className={`bk-page-btn bk-page-num ${page === p ? 'active' : ''}`}
                onClick={() => setPage(p)}
              >
                {p}
              </button>
            );
          })}
          <button
            className="bk-page-btn"
            disabled={page === totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        </div>
      </div>

      {/* Create / Edit Modal */}
      {modal && (
        <BookingModal
          booking={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSave={handleSave}
        />
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="bk-confirm-overlay" onClick={e => e.target === e.currentTarget && setDeleteTarget(null)}>
          <div className="bk-confirm">
            <div className="bk-confirm-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/></svg>
            </div>
            <h3>Delete Booking</h3>
            <p>Are you sure you want to delete booking <strong>#{deleteTarget.id}</strong> for <strong>{deleteTarget.full_name}</strong>? This action cannot be undone.</p>
            <div className="bk-confirm-actions">
              <button className="bk-btn-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button className="bk-btn-delete" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading ? <span className="bk-spinner-sm" /> : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Detail Panel */}
      {viewTarget && (
        <div className="bk-confirm-overlay" onClick={e => e.target === e.currentTarget && setViewTarget(null)}>
          <div className="bk-view-panel">
            <div className="bk-view-header">
              <div>
                <span className="bk-tag-sm">#{viewTarget.id}</span>
                <h3>{viewTarget.full_name}</h3>
              </div>
              <button className="bm-close" onClick={() => setViewTarget(null)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="bk-view-body">
              {[
                ['Email', viewTarget.email],
                ['Phone', viewTarget.phone || '—'],
                ['Date', fmt(viewTarget.date)],
                ['Ticket Type', TYPE_LABEL[viewTarget.ticket_type] || viewTarget.ticket_type],
                ['Quantity', viewTarget.quantity],
                ['Payment', viewTarget.payment_method],
                ['Booked On', fmt(viewTarget.created_at)],
              ].map(([label, val]) => (
                <div className="bk-view-row" key={label}>
                  <span className="bk-view-label">{label}</span>
                  <span className="bk-view-val">{val}</span>
                </div>
              ))}
            </div>
            <div className="bk-view-footer">
              <button className="bk-btn-cancel" onClick={() => setViewTarget(null)}>Close</button>
              <button className="bk-btn-new" onClick={() => { setViewTarget(null); setModal(viewTarget); }}>
                Edit Booking
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </PageLayout>
  );
}